/**
 * OfferService — client cache + event bridge for the offer engine (spec §8).
 *
 * The server pushes the player's personalised live set (OFFERS_ACTIVE) once
 * after auth, and a pipeline-change ping (OFFERS_UPDATE) whenever ops edit the
 * pipeline. This service holds the current set, re-requests on that ping and on
 * app foreground/resume, and emits 'offers:changed' for the UI (StoreScreen
 * OFFERS tab + the startup popup, built in step 3).
 *
 * Offers are ordered by the server: free + video first, then ONE random paid.
 * The client renders them in order — the popup shows free/video on start and
 * the single paid card; the store tab lists them all.
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import type { NetworkManager } from '../network/NetworkManager';
import type { OfferWire, OfferClaimResult } from '@sdl/shared';

interface OfferServiceEvents {
  'offers:changed': OfferWire[];
  'offer:claim_result': OfferClaimResult;
}

export class OfferService extends EventEmitter<OfferServiceEvents> {
  private networkManager: NetworkManager;
  private offers: OfferWire[] = [];

  constructor(networkManager: NetworkManager) {
    super();
    this.networkManager = networkManager;
    this.setupListeners();
  }

  private setupListeners(): void {
    this.networkManager.on('offers:active', ({ offers }) => {
      this.offers = offers ?? [];
      this.emit('offers:changed', this.offers);
    });

    // Pipeline changed → pull our personalised set again (eligibility is per-player).
    this.networkManager.on('offers:update', () => {
      this.networkManager.requestOffers();
    });

    this.networkManager.on('offer:claim_result', (result) => {
      this.emit('offer:claim_result', result);
    });
  }

  /** Current live offer set (server-ordered). */
  getOffers(): OfferWire[] {
    return this.offers;
  }

  /** Free + video offers (shown first, always). */
  getFreeAndVideoOffers(): OfferWire[] {
    return this.offers.filter((o) => o.kind === 'free' || o.kind === 'video');
  }

  /** The single paid offer in the set (if any). */
  getPaidOffer(): OfferWire | undefined {
    return this.offers.find((o) => o.kind === 'paid');
  }

  /** Re-pull on app foreground / store open. */
  refresh(): void {
    this.networkManager.requestOffers();
  }
}
